/**
 * ============================================================================
 * DASHBOARD SWITCHER — the served-dashboard picker (spec multi-dashboard R2).
 * ============================================================================
 *
 * Rendered at the right of the page-tabs strip, next to the page tabs it
 * scopes. Lists ALL served dashboards from GetPermissions ("KFE · WBR",
 * "Quality · WBR"; name from the API, review type = supportedReviewTypes[0]).
 * Inaccessible dashboards render DISABLED (Bindle "request access" affordance
 * tracked P1). Selecting one navigates to its URL — DashboardGate mirrors the
 * slug into the store (the URL is the source of truth, spec R5) — so switching
 * is instant (data caches key on the id). While permissions load (or if the
 * API errors / isn't deployed), the default-only fallback keeps the control honest.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import ButtonDropdown from '@amzn/awsui-components-core/button-dropdown';
import { useAppSelector } from 'src/app/store';
import {
  selectServedDashboards,
  useGetPermissionsQuery,
} from 'src/features/api/permissionsApi';
import { dashboardPath } from 'src/config/dashboards';
import { WBR_REVIEW_TYPE } from 'src/constants';

export const DashboardSwitcher: React.FC = () => {
  const navigate = useNavigate();
  const activeDashboardId = useAppSelector(
    (state) => state.dashboard.activeDashboardId,
  );
  const { data: permissions } = useGetPermissionsQuery();
  const dashboards = selectServedDashboards(permissions);
  const active =
    dashboards.find((d) => d.dashboardId === activeDashboardId) ??
    dashboards[0];
  const reviewTypeOf = (d: (typeof dashboards)[number]) =>
    d.supportedReviewTypes[0] ?? WBR_REVIEW_TYPE;

  return (
    <div className="page-tabs__switcher">
      <ButtonDropdown
        ariaLabel="Dashboard selector"
        items={dashboards.map((d) => ({
          id: d.dashboardId,
          text: `${d.name} · ${reviewTypeOf(d)}`,
          disabled: !d.accessible,
        }))}
        onItemClick={({ detail }) => {
          if (detail.id === activeDashboardId) return;
          // Navigate to the dashboard's URL; DashboardGate mirrors it into the
          // store. The URL is the source of truth (spec R5).
          const picked = dashboards.find((d) => d.dashboardId === detail.id);
          navigate(
            dashboardPath(
              detail.id,
              '/',
              picked ? reviewTypeOf(picked) : WBR_REVIEW_TYPE,
            ),
          );
        }}
      >
        {`${active.name} · ${reviewTypeOf(active)}`}
      </ButtonDropdown>
    </div>
  );
};
